import { FiChevronLeft } from 'react-icons/fi';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import PieChartWidget from './PieChartWidget';
import { Loader } from '@/components/custom/loader';
import usePermission from '@/hooks/usePermission';
import { useSession } from '@/hooks/useSession';
import { GreTable } from './GreTable';
import SchoolTable from './SchoolTable';
import StudentsTable from './StudentsTable';
import { EmptyState } from '@/components/custom/empty-state';
import { getReportFileName } from '@/utils/getReportFileName';

export function PerformanceReport() {
  const { session } = useSession();
  const token = session?.user?.authToken;
  const navigate = useNavigate();
  const client = useClient();

  const [courses, setCourses] = useState([]);
  const [kpisData, setKpisData] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ field: null, direction: 'asc' });

  const [selectedGre, setSelectedGre] = useState(null);
  const [selectedSchool, setSelectedSchool] = useState(null);

  const filters = useFormik({
    initialValues: {
      'start-date': moment().subtract(30, 'days').format('YYYY-MM-DD'),
      'end-date': moment().format('YYYY-MM-DD'),
      course: null,
      searchTerm: ''
    }
  });

  const handleSort = field => {
    setSortConfig(prev => {
      if (prev.field === field) {
        return {
          field,
          direction: prev.direction === 'asc' ? 'desc' : 'asc'
        };
      }

      return {
        field,
        direction: 'asc'
      };
    });
  };

  const resetSort = () => {
    setSortConfig({ field: null, direction: 'asc' });
  };

  const kpis = [
    {
      id: 1,
      title: 'Alunos Ativos',
      value: kpisData?.['active_students'] !== null ? kpisData['active_students'] : '--',
      variation: 'report-kpi-card--analytics'
    },
    {
      id: 2,
      title: 'Desempenho médio',
      value: kpisData?.['average_performance'] !== null ? `${kpisData['average_performance']}%` : '--',
      variation: 'report-kpi-card--analytics'
    },
    {
      id: 3,
      title: 'Área com maior desempenho',
      value: kpisData?.['best_area'] ? kpisData['best_area'] : '--',
      variation: 'report-kpi-card--analytics'
    },
    {
      id: 4,
      title: 'Área com menor desempenho',
      value: kpisData?.['worst_area'] ? kpisData['worst_area'] : '--',
      variation: 'report-kpi-card--analytics'
    }
  ];

  const getKpisData = async () => {
    setLoading(true);
    let url = `/reports/performance360/kpis?initial_date=${filters.values['start-date']}&end_date=${filters.values['end-date']}`;

    if (filters.values.course) {
      url += `&course_ids=${filters.values.course}`;
    }

    try {
      const { data, error } = await client.fetch(url);
      if (error?.message === 'Parâmetros de data inválidos') {
        setKpisData([]);
        setLoading(false);
        return;
      }
      setKpisData(data);
    } catch (error) {
      const errorMessage = 'Erro ao buscar os dados do relatório';
      toast.error(errorMessage);
    }
    setLoading(false);
  };

  const getReportData = async () => {
    setLoading(true);

    let url = `/reports/performance360/`;

    if (!selectedGre && !selectedSchool) {
      url += `regional_ranking?initial_date=${filters.values['start-date']}&end_date=${filters.values['end-date']}`;
    }
    if (selectedGre && !selectedSchool) {
      url += `gre_ranking/${selectedGre?.['regional_id']}?initial_date=${filters.values['start-date']}&end_date=${filters.values['end-date']}`;
    }
    if (selectedGre && selectedSchool) {
      url += `institution_ranking/${selectedSchool?.['institution_id']}?initial_date=${filters.values['start-date']}&end_date=${filters.values['end-date']}`;
    }

    if (filters.values.course) {
      url += `&course_ids=${filters.values.course}`;
    }

    try {
      const { data, error } = await client.fetch(url);
      if (error?.message === 'Parâmetros de data inválidos') {
        toast.error('O intervalo entre a data inicial e final deve ser de no máximo 30 dias');
        setLoading(false);
        setReportData([]);
        return;
      }
      setReportData(data);
    } catch (error) {
      const errorMessage = 'Erro ao buscar os dados do relatório';
      toast.error(errorMessage);
    }
    setLoading(false);
  };

  const fetchCourses = async (url = '/courses/simple') => {
    const { data, error } = await client.fetch(url);
    if (error) {
      toast.error('Falha ao carregar cursos');
    } else {
      setCourses(data.map(item => ({ value: item.id, label: item.title })));
    }
  };

  const handleDownload = () => {
    setIsDownloading(true);

    let url = `${process.env.REACT_APP_DEV_URL}/reports/performance360/`;

    if (!selectedGre && !selectedSchool) {
      url += `regional_ranking?initial_date=${filters.values['start-date']}&end_date=${filters.values['end-date']}`;
    }
    if (selectedGre && !selectedSchool) {
      url += `gre_ranking/${selectedGre?.['regional_id']}?initial_date=${filters.values['start-date']}&end_date=${filters.values['end-date']}`;
    }
    if (selectedGre && selectedSchool) {
      url += `institution_ranking/${selectedSchool?.['institution_id']}?initial_date=${filters.values['start-date']}&end_date=${filters.values['end-date']}`;
    }

    if (filters.values.course) {
      url += `&course_ids=${filters.values.course}`;
    }

    fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Erro ao baixar o relatório');
        }

        const disposition = response.headers.get('content-disposition');
        const fileName = getReportFileName(disposition);

        return response.blob().then(blob => ({ blob, fileName }));
      })
      .then(({ blob, fileName }) => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
        toast.success('Relatório baixado com sucesso!');
      })
      .catch(error => {
        console.error('Erro no download:', error);
        toast.error('Erro ao baixar o relatório');
      })
      .finally(() => {
        setIsDownloading(false);
      });
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    getKpisData();
    getReportData();
  }, [filters.values['start-date'], filters.values['end-date'], filters.values.course]);

  useEffect(() => {
    filters.setFieldValue('searchTerm', '');
    getReportData();
    resetSort();
  }, [selectedGre, selectedSchool]);

  const getColor = value => {
    if (value > 66) {
      return 'text-[#1CA388]';
    } else if (value > 33 && value <= 66) {
      return 'text-[#FDB462]';
    } else if (value <= 33) {
      return 'text-[#FF726C]';
    }
  };

  return (
    <div className="report">
      <header className="report__header">
        <h1 className="report__title">
          <button
            className="card__icon-wrapper"
            aria-label="Voltar"
            title="Voltar"
            onClick={() => navigate('/metricas/institucional')}
          >
            <FiChevronLeft className="card__icon" />
          </button>
          Performance 360
        </h1>
        <p className="report__description">Acompanhe o desempenho e a participação dos estudantes por regional, escola e aluno.</p>
      </header>

      <main className="report__body">
        <div
          className="report-filter__select"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex gap-3">
            <Select
              openMenuOnFocus
              options={[{ value: null, label: 'Todos os cursos' }, ...courses]}
              className="react-multi-select report-form__select"
              classNamePrefix="react-multi-select"
              placeholder={'Selecione um curso'}
              noOptionsMessage={() => 'Sem opções'}
              components={{
                IndicatorSeparator: () => null,
                ClearIndicator: () => null
              }}
              onChange={e => filters.setFieldValue('course', e.value)}
            />
            <div className="report-form__datepicker">
              <input
                className="report-form__control"
                id="start-date"
                name="start-date"
                type="date"
                value={moment(filters.values['start-date']).format('YYYY-MM-DD')}
                onChange={e => filters.setFieldValue('start-date', e.target.value)}
                title="Data de início"
                aria-label="Data de início"
                min={moment().startOf('year').format('YYYY-MM-DD')}
              />
              <input
                className="report-form__control"
                id="end-date"
                name="end-date"
                type="date"
                value={moment(filters.values['end-date']).format('YYYY-MM-DD')}
                onChange={e => filters.setFieldValue('end-date', e.target.value)}
                min={moment(filters.values['start-date']).format('YYYY-MM-DD')}
                title="Data de fim"
                aria-label="Data de fim"
              />
            </div>
          </div>
        </div>

        {loading && <Loader />}

        {!loading && (reportData?.ranking?.length === 0 || reportData?.institutions?.length === 0 || reportData?.students?.length === 0) && (
          <EmptyState
            type="data"
            text="Não há dados para exibir no gráfico."
            className="my-10"
          />
        )}

        {!loading && (reportData?.ranking?.length > 0 || reportData?.institutions?.length > 0 || reportData?.students?.length > 0) && (
          <div className="report-widget__body gap-12">
            <div className="report-kpi-card-wrapper">
              {kpis.map(kpi => (
                <div
                  className={`report-kpi-card ${kpi.variation}`}
                  key={kpi.id}
                >
                  <h3 className="report-kpi-card__title">{kpi.title}</h3>
                  <p
                    className="report-kpi-card__value"
                    title={kpi.value}
                  >
                    {kpi.value}
                  </p>
                </div>
              ))}
            </div>
            {/* Gráfico de GREs */}
            {!selectedGre && !selectedSchool && (
              <PieChartWidget
                data={reportData?.['chart_data']}
                currentLevel="gre"
                setSelectedGre={setSelectedGre}
                setSelectedSchool={setSelectedSchool}
                selectedGre={selectedGre}
                selectedSchool={selectedSchool}
              />
            )}
            {/* Gráfico de escolas */}
            {selectedGre && !selectedSchool && (
              <PieChartWidget
                data={reportData?.['chart_data']}
                currentLevel="schools"
                setSelectedGre={setSelectedGre}
                setSelectedSchool={setSelectedSchool}
                selectedGre={selectedGre}
                selectedSchool={selectedSchool}
              />
            )}
            {/* Gráfico da escola selecionada */}
            {selectedGre && selectedSchool && (
              <PieChartWidget
                data={[{ ...selectedSchool, chart_percentage: 100 }]}
                currentLevel="school"
                setSelectedGre={setSelectedGre}
                setSelectedSchool={setSelectedSchool}
                selectedGre={selectedGre}
                selectedSchool={selectedSchool}
              />
            )}

            <div className="report-widget">
              <div className="report-widget__header">
                <h3 className="report-widget__title">Ranking {selectedSchool ? 'por aluno' : 'de desempenho'}</h3>
                <div className="report-widget__filter">
                  {usePermission('Download de relatórios', 'Relatórios') && (
                    <button
                      className="btn btn--outline"
                      type="button"
                      onClick={handleDownload}
                    >
                      {isDownloading ? 'Baixando...' : 'Download do relatório'}
                    </button>
                  )}
                </div>
              </div>

              {!selectedGre && !selectedSchool && (
                <GreTable
                  data={reportData}
                  filters={filters}
                  setSelectedGre={setSelectedGre}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                  getColor={getColor}
                />
              )}

              {selectedGre && !selectedSchool && (
                <SchoolTable
                  data={reportData}
                  selectedGre={selectedGre}
                  setSelectedGre={setSelectedGre}
                  setSelectedSchool={setSelectedSchool}
                  filters={filters}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                  getColor={getColor}
                />
              )}
              {selectedGre && selectedSchool && (
                <StudentsTable
                  data={reportData}
                  selectedGre={selectedGre}
                  selectedSchool={selectedSchool}
                  setSelectedGre={setSelectedGre}
                  setSelectedSchool={setSelectedSchool}
                  filters={filters}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                  getColor={getColor}
                />
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
